import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { FeeServiceApi, FeeService, NewFeeService } from '../models';
import { mapFeeServiceApiToFeeService } from '../adapters';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { RootState } from '@/store/store';

import { API_FLEET_URL } from '@/utils';

export interface FeeServicesFilter {
  name?: string;
  businessId?: string;
  serviceTypeId?: string;
  vehicleTypeId?: string;
}

const feeServicesService = createApi({
  reducerPath: 'feeServicesService',
  baseQuery: fetchBaseQuery({ baseUrl: `${API_FLEET_URL}/fee-services`
    ,
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as RootState;

      const token = state.auth?.auth?.accessToken || null;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },}),
  tagTypes: ['FeeService', 'FeeServices'],
  endpoints: (builder) => ({
    getFeeService: builder.query<FeeService, { feeServiceId: string }>({
      query: ({ feeServiceId }) => ({
        url: `/${feeServiceId}`,
      }),
      transformResponse: (response: FeeServiceApi) => mapFeeServiceApiToFeeService(response),
      providesTags: ['FeeService'],
    }),
    getFeeServices: builder.query<PaginatedResponse<FeeService>, { filters?: FeeServicesFilter; paginated: PaginatedRequest; }>({
      query: ({ filters, paginated }) => ({
        url: '',
        params: {
          name: filters?.name,
          commercialBusinessId: filters?.businessId,
          serviceTypeId: filters?.serviceTypeId,
          vehicleTypeId: filters?.vehicleTypeId,
          offset: (paginated.page - 1) * paginated.pageSize,
          limit: paginated.pageSize,
        },
      }),
      transformResponse: (response: PaginatedResponse<FeeServiceApi>) => ({
        ...response,
        items: response.items.map(mapFeeServiceApiToFeeService),
      }),
      providesTags: ['FeeServices']
    }),
    createFeeService: builder.mutation<FeeService, NewFeeService>({
      query: (data) => ({
        url: '',
        method: 'POST',
        body: { ...data, commercialBusinessId: data.businessId },
      }),
      invalidatesTags: ['FeeServices'],
    }),
    updateFeeService: builder.mutation<FeeService, { feeServiceId: string; data: NewFeeService }>({
      query: ({ feeServiceId, data }) => ({
        url: `/${feeServiceId}`,
        method: 'PUT',
        body: { ...data, commercialBusinessId: data.businessId },
      }),
      invalidatesTags: ['FeeService', 'FeeServices'],
    }),
    deleteFeeService: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FeeService', 'FeeServices'],
    }),
  }),
});

export default feeServicesService;

export const {
  useGetFeeServiceQuery,
  useGetFeeServicesQuery,
  useCreateFeeServiceMutation,
  useUpdateFeeServiceMutation,
  useDeleteFeeServiceMutation,
} = feeServicesService;
